
import type React from "react"

import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { PlusIcon, MinusIcon, Loader2 } from "lucide-react"
import { useExercisePlan, useUpdateExercisePlan } from "@/api/exercise-plans"
import { ScrollArea } from "@/components/ui/scroll-area"

interface EditExercisePlanDialogProps {
  planId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditExercisePlanDialog({ planId, open, onOpenChange }: EditExercisePlanDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()

  // Skip fetching if planId is empty or dialog is not open
  const {
    data: plan,
    isLoading,
    error,
  } = useExercisePlan(open && planId ? planId : "", {
    enabled: !!planId && open,
  })
  const updateExercisePlan = useUpdateExercisePlan()

  const [exercises, setExercises] = useState<any[]>([])
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    difficulty: "beginner" as "beginner" | "intermediate" | "advanced",
    duration: "",
    targetMuscles: "",
    equipment: "",
    description: "",
  })

  useEffect(() => {
    if (plan && open) {
      console.log("Plan data loaded:", plan)
      setFormData({
        name: plan.name || "",
        type: plan.type || "",
        difficulty: plan.difficulty || "beginner",
        duration: plan.duration ? plan.duration.toString() : "0",
        targetMuscles: Array.isArray(plan.targetMuscles) ? plan.targetMuscles.join(", ") : "",
        equipment: Array.isArray(plan.equipment) ? plan.equipment.join(", ") : "",
        description: plan.description || "",
      })

      setExercises(
        Array.isArray(plan.exercises)
          ? plan.exercises.map((ex: { name?: string; sets?: number; reps?: string; rest?: string; description?: string }) => ({
              name: ex.name || "",
              sets: ex.sets || 3,
              reps: ex.reps || "",
              rest: ex.rest || "",
              description: ex.description || "",
            }))
          : [{ name: "", sets: 3, reps: "", rest: "", description: "" }],
      )
    }
  }, [plan, open])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target
    setFormData((prev) => ({ ...prev, [id]: value }))
  }

  const handleSelectChange = (value: string) => {
    setFormData((prev) => ({ ...prev, difficulty: value as "beginner" | "intermediate" | "advanced" }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!planId) {
      console.error("No plan ID provided")
      return
    }

    try {
      console.log("Submitting form with data:", {
        _id: planId, // Use _id instead of id
        ...formData,
        duration: Number.parseInt(formData.duration || "0"),
        targetMuscles: formData.targetMuscles.split(",").map((item) => item.trim()),
        equipment: formData.equipment.split(",").map((item) => item.trim()),
        exercises: exercises.map((ex) => ({
          name: ex.name,
          sets: Number.parseInt((ex.sets || 3).toString()),
          reps: ex.reps || "",
          rest: ex.rest || "",
          description: ex.description || "",
        })),
      })

      await updateExercisePlan.mutateAsync({
        _id: planId, // Use _id instead of id
        name: formData.name,
        type: formData.type,
        difficulty: formData.difficulty,
        duration: Number.parseInt(formData.duration || "0"),
        targetMuscles: formData.targetMuscles.split(",").map((item) => item.trim()),
        equipment: formData.equipment.split(",").map((item) => item.trim()),
        description: formData.description,
        exercises: exercises.map((ex) => ({
          name: ex.name,
          sets: Number.parseInt((ex.sets || 3).toString()),
          reps: ex.reps || "",
          rest: ex.rest || "",
          description: ex.description || "",
        })),
      })

      // Close dialog
      onOpenChange(false)
    } catch (error) {
      console.error("Error updating exercise plan:", error)
      toast({
        title: t("common.error"),
        description: t("exercisePlans.errorUpdatingPlan"),
        variant: "destructive",
      })
    }
  }

  const addExercise = () => {
    setExercises([...exercises, { name: "", sets: 3, reps: "", rest: "", description: "" }])
  }

  const removeExercise = (index: number) => {
    if (exercises.length > 1) {
      const newExercises = [...exercises]
      newExercises.splice(index, 1)
      setExercises(newExercises)
    }
  }

  const handleExerciseChange = (index: number, field: string, value: string | number) => {
    const updatedExercises = [...exercises]
    updatedExercises[index] = { ...updatedExercises[index], [field]: value }
    setExercises(updatedExercises)
  }

  // Don't render anything if dialog is not open
  if (!open) {
    return null
  }

  // Show error state
  if (error) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("common.error")}</DialogTitle>
            <DialogDescription>{t("exercisePlans.errorLoadingPlan")}</DialogDescription>
          </DialogHeader>
          <div className="py-4 text-destructive">{error instanceof Error ? error.message : "Unknown error"}</div>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>{t("common.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // Show loading state
  if (isLoading || !plan) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="flex justify-center items-center h-40">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("exercisePlans.editPlan")}</DialogTitle>
          <DialogDescription>{t("exercisePlans.editPlanDescription")}</DialogDescription>
        </DialogHeader>

        <form id="edit-exercise-plan-form" onSubmit={handleSubmit}>
          <ScrollArea className="h-[500px] pr-4">
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  {t("exercisePlans.name")}
                </Label>
                <Input id="name" value={formData.name} onChange={handleInputChange} className="col-span-3" required />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="type" className="text-right">
                  {t("exercisePlans.type")}
                </Label>
                <Input
                  id="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="col-span-3"
                  placeholder="Strength, Cardio, Flexibility, etc."
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="difficulty" className="text-right">
                  {t("exercisePlans.difficulty")}
                </Label>
                <div className="col-span-3">
                  <Select value={formData.difficulty} onValueChange={handleSelectChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">{t("exercisePlans.beginner")}</SelectItem>
                      <SelectItem value="intermediate">{t("exercisePlans.intermediate")}</SelectItem>
                      <SelectItem value="advanced">{t("exercisePlans.advanced")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="duration" className="text-right">
                  {t("exercisePlans.duration")}
                </Label>
                <div className="col-span-3 flex items-center gap-2">
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className="flex-1"
                    required
                  />
                  <span>{t("exercisePlans.minutes")}</span>
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="targetMuscles" className="text-right">
                  {t("exercisePlans.targetMuscles")}
                </Label>
                <Input
                  id="targetMuscles"
                  value={formData.targetMuscles}
                  onChange={handleInputChange}
                  className="col-span-3"
                  placeholder="Chest, Back, Legs, etc. (comma separated)"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="equipment" className="text-right">
                  {t("exercisePlans.equipment")}
                </Label>
                <Input
                  id="equipment"
                  value={formData.equipment}
                  onChange={handleInputChange}
                  className="col-span-3"
                  placeholder="Dumbbells, Barbell, Bench, etc. (comma separated)"
                />
              </div>
              <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="description" className="text-right pt-2">
                  {t("exercisePlans.description")}
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="col-span-3"
                  rows={3}
                  required
                />
              </div>

              <div className="border-t pt-4 mt-2">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">{t("exercisePlans.exercises")}</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addExercise}>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    {t("exercisePlans.addExercise")}
                  </Button>
                </div>

                {exercises.map((exercise, index) => (
                  <div key={index} className="border rounded-md p-4 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium">
                        {t("exercisePlans.exercise")} {index + 1}
                      </h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeExercise(index)}
                        disabled={exercises.length <= 1}
                      >
                        <MinusIcon className="h-4 w-4" />
                        <span className="sr-only">{t("exercisePlans.removeExercise")}</span>
                      </Button>
                    </div>

                    <div className="grid gap-3">
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor={`exercise-${index}-name`} className="text-right">
                          {t("exercisePlans.exerciseName")}
                        </Label>
                        <Input
                          id={`exercise-${index}-name`}
                          value={exercise.name}
                          onChange={(e) => handleExerciseChange(index, "name", e.target.value)}
                          className="col-span-3"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor={`exercise-${index}-sets`} className="text-right">
                          {t("exercisePlans.sets")}
                        </Label>
                        <Input
                          id={`exercise-${index}-sets`}
                          type="number"
                          min="1"
                          value={exercise.sets}
                          onChange={(e) => handleExerciseChange(index, "sets", Number.parseInt(e.target.value || "3"))}
                          className="col-span-3"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor={`exercise-${index}-reps`} className="text-right">
                          {t("exercisePlans.reps")}
                        </Label>
                        <Input
                          id={`exercise-${index}-reps`}
                          value={exercise.reps}
                          onChange={(e) => handleExerciseChange(index, "reps", e.target.value)}
                          className="col-span-3"
                          placeholder="10, 8-12, etc."
                          required
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor={`exercise-${index}-rest`} className="text-right">
                          {t("exercisePlans.rest")}
                        </Label>
                        <Input
                          id={`exercise-${index}-rest`}
                          value={exercise.rest}
                          onChange={(e) => handleExerciseChange(index, "rest", e.target.value)}
                          className="col-span-3"
                          placeholder="60s, 90s, etc."
                          required
                        />
                      </div>
                      <div className="grid grid-cols-4 items-start gap-4">
                        <Label htmlFor={`exercise-${index}-description`} className="text-right pt-2">
                          {t("exercisePlans.exerciseDescription")}
                        </Label>
                        <Textarea
                          id={`exercise-${index}-description`}
                          value={exercise.description}
                          onChange={(e) => handleExerciseChange(index, "description", e.target.value)}
                          className="col-span-3"
                          rows={2}
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="edit-exercise-plan-form" disabled={updateExercisePlan.isPending}>
            {updateExercisePlan.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("common.saving")}
              </>
            ) : (
              t("common.save")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
